import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './index.css';
import './i18n';

// Service Worker Registration with error handling
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration);
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

// Enhanced chunk loading error handler
let hasReloaded = false;
const MAX_RETRIES = 3;
const retryCount: { [key: string]: number } = {};

window.addEventListener('error', (event) => {
  const target = event.target as HTMLElement;
  const isChunkError = event.message?.includes('Loading chunk') || 
                       event.message?.includes('Failed to fetch') ||
                       target?.tagName === 'SCRIPT';

  if (isChunkError && !hasReloaded) {
    console.warn('Chunk loading failed, attempting recovery...');
    hasReloaded = true;
    
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      }).then(() => {
        window.location.reload();
      });
    } else {
      window.location.reload();
    }
  }
});

// Network status monitoring
let isOnline = navigator.onLine;
let offlineNotificationShown = false;

const showNetworkStatus = (online: boolean) => {
  const existingNotification = document.getElementById('network-status-notification');
  if (existingNotification) {
    existingNotification.remove();
  }

  const notification = document.createElement('div');
  notification.id = 'network-status-notification';
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 16px 24px;
    border-radius: 12px;
    color: white;
    font-weight: 600;
    z-index: 99999;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    animation: slideIn 0.3s ease-out;
    display: flex;
    align-items: center;
    gap: 12px;
    ${online 
      ? 'background: linear-gradient(135deg, #10b981 0%, #059669 100%);' 
      : 'background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);'
    }
  `;

  const icon = document.createElement('i');
  icon.className = online ? 'ri-wifi-line' : 'ri-wifi-off-line';
  icon.style.fontSize = '24px';

  const text = document.createElement('span');
  text.textContent = online ? 'Back Online!' : 'No Internet Connection';

  notification.appendChild(icon);
  notification.appendChild(text);
  document.body.appendChild(notification);

  if (online) {
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
};

window.addEventListener('online', () => {
  isOnline = true;
  offlineNotificationShown = false;
  showNetworkStatus(true);
  
  // Reload if page failed to load while offline
  if (document.body.innerHTML.includes('No Internet Connection') || 
      document.body.innerHTML.includes('Loading Failed')) {
    setTimeout(() => window.location.reload(), 1000);
  }
});

window.addEventListener('offline', () => {
  isOnline = false;
  if (!offlineNotificationShown) {
    showNetworkStatus(false);
    offlineNotificationShown = true;
  }
});

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(400px);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Performance monitoring
const measurePerformance = () => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      const loadTime = navigation.loadEventEnd - navigation.fetchStart;
      console.log(`Page load time: ${loadTime}ms`);
      
      // If load time is too slow, suggest clearing cache
      if (loadTime > 10000) {
        console.warn('Slow page load detected. Consider clearing browser cache.');
      }
    }
  }
};

window.addEventListener('load', measurePerformance);

// Render app with error boundary
const rootElement = document.getElementById('root');

if (!rootElement) {
  document.body.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); font-family: system-ui, -apple-system, sans-serif;">
      <div style="text-align: center; color: white; padding: 40px;">
        <i class="ri-error-warning-line" style="font-size: 64px; margin-bottom: 20px;"></i>
        <h1 style="font-size: 32px; margin-bottom: 16px;">Application Error</h1>
        <p style="font-size: 18px; margin-bottom: 24px; opacity: 0.9;">Unable to find root element</p>
        <button onclick="window.location.reload()" style="background: white; color: #667eea; padding: 12px 32px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; font-size: 16px;">
          Reload Page
        </button>
      </div>
    </div>
  `;
} else {
  try {
    const root = createRoot(rootElement);
    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  } catch (error) {
    console.error('Failed to render app:', error);
    rootElement.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; min-height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); font-family: system-ui, -apple-system, sans-serif;">
        <div style="text-align: center; color: white; padding: 40px; max-width: 500px;">
          <i class="ri-error-warning-line" style="font-size: 64px; margin-bottom: 20px;"></i>
          <h1 style="font-size: 32px; margin-bottom: 16px;">Failed to Load</h1>
          <p style="font-size: 18px; margin-bottom: 24px; opacity: 0.9;">The application failed to start. This might be due to a network issue or browser cache problem.</p>
          <div style="display: flex; gap: 12px; justify-content: center;">
            <button onclick="window.location.reload()" style="background: white; color: #667eea; padding: 12px 32px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; font-size: 16px;">
              Reload Page
            </button>
            <button onclick="if(confirm('This will clear your browser cache. Continue?')){caches.keys().then(names=>Promise.all(names.map(name=>caches.delete(name)))).then(()=>window.location.reload())}" style="background: rgba(255,255,255,0.2); color: white; padding: 12px 32px; border-radius: 8px; border: none; font-weight: 600; cursor: pointer; font-size: 16px;">
              Clear Cache & Reload
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
